import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { DialogService } from 'src/app/common/dialog/dialog.service';
import { filter } from 'rxjs/operators';
import { FilterHistoryService } from '../../service/filter-history.service';
import { DataSource } from 'src/app/shared/model/data-source';
import { TranslateService } from '@ngx-translate/core';
import { RequestCriteria } from 'src/app/shared/model/request-criteria';
import { Operator } from 'src/app/shared/model/operator';
import { flatten } from '@angular/compiler';
import { Subscription } from 'rxjs';
import { toVisibleDateTimeFormat } from 'src/app/shared/util/date-util';
import { Option } from 'src/app/settings/model/value-field';
import { FilterHistoryManager } from '../../service/filter-history-manager';

@Component({
  selector: 'app-filter-history',
  templateUrl: './filter-history.component.html',
  styleUrls: ['./filter-history.component.scss']
})
export class FilterHistoryComponent implements OnDestroy {

  @Input() saveDisabled: boolean;
  @Input() manualMode: boolean = false;
  @Output() activate = new EventEmitter();
  @Output() navigate = new EventEmitter();

  private tooltips: Record<number, string> = {};
  private tooltipFormatersMap: Map<string, TooltipFormatter> = new Map();
  private dataSource: DataSource;
  private historyChangeSub: Subscription;
  private historyState: FilterHistoryManager;
  private historyNavigateSub: Subscription;
  private clientId: string;

  constructor(private filterHistoryService: FilterHistoryService, private dialog: DialogService, private translate: TranslateService) { }

  ngOnDestroy(): void {
    this.historyChangeSub?.unsubscribe();
    this.historyNavigateSub?.unsubscribe();
    if (this.dataSource)
      this.filterHistoryService.remove(this.dataSource.name);
  }

  init(dataSource: DataSource, clientId: string, withoutInit: boolean = false): void {
    if (dataSource && dataSource !== this.dataSource && this.filterHistoryService.isEnable) {
      this.historyChangeSub?.unsubscribe();
      this.historyNavigateSub?.unsubscribe();

      this.dataSource = dataSource;
      this.clientId = clientId;
      this.historyState = this.filterHistoryService.getManager(dataSource.name);
      if (withoutInit) this.historyState.setRecent(-1);
      queueMicrotask(() => {
        this.createTooltipFormatters();
        this.createTooltipViews();
      });
      this.historyChangeSub = this.historyState.changeSub$.subscribe({
        next: () => this.createTooltipViews()
      });

      this.historyNavigateSub = this.historyState.getNavigateObs$(clientId).subscribe({
        next: criteria => this.navigate.emit(criteria)
      });
    }
  }

  handleActivate(event: Event): void {
    if (!this.manualMode) {
      this.activate.emit();
      event.stopPropagation();
    }
  }

  get recentTimestamp(): Date {
    return this.historyState.recentTimestamp;
  };

  get hasNext(): boolean {
    return this.historyState?.hasNext;
  };

  get hasPrevious(): boolean {
    return this.historyState?.hasPrevious;
  };

  get isEmpty(): boolean {
    return this.historyState.isEmpty;
  }

  get isEnable(): boolean {
    return this.dataSource && this.filterHistoryService.isEnable;
  }

  get history(): Date[] {
    return this.historyState.historyChain;
  }

  get recentId(): number {
    return this.historyState.recentId;
  }

  getTooltip(idx: number): string {
    return this.tooltips[idx];
  }

  next(): void {
    if (this.hasNext) {
      this.historyState.next();
    }
  }

  previous(): void {
    if (this.hasPrevious) {
      this.historyState.previous();
    }
  }

  save(): void {
    this.historyState?.save();
  }

  setRecent(event: MatSelectChange): void {
    this.historyState?.setRecent(event.value);
  }

  getRecent(): RequestCriteria {
    return this.historyState?.getRecent(this.clientId);
  }

  put(criteria: RequestCriteria): void {
    this.historyState?.put(this.clientId, criteria);
  }

  clear(): void {
    this.dialog.openConfirmationDialog('confirmation-clear-filter-history')
      .afterClosed()
      .pipe(filter(value => value))
      .subscribe({
        next: () => {
          this.historyState.clear();
        }
      });
  }

  private createTooltipViews(): void {
    this.tooltips = {};
    this.historyState.getHistory().forEach((c, idx) => {
      const toView = Object.entries(c.slot).map(([_, value]) => value);
      if (toView?.length) {
        const first = toView?.[0];
        const flat = toView?.length > 1 ? flatten(toView.map(cr => cr.criteria ?? [])) : first?.criteria;
        if (first) first.criteria = flat;
        const formatted = this.formatTooltip(first);
        this.tooltips[idx] = formatted && /\S/.test(formatted) ? formatted : this.translate.instant('none');
      } else this.tooltips[idx] = this.translate.instant('none');
    });
  }

  private createTooltipFormatters(): void {
    this.tooltipFormatersMap.clear();
    const cols = this.dataSource.fields;
    cols.forEach(dField => {
      const field = dField.valueField;
      const col = dField.column;
      const colName = col.name;
      const name = this.translate.instant(col.title ?? col.name);
      const format = col.format ? col.format.bind(this) : null;
      const translatable = col.translatable;
      this.tooltipFormatersMap.set(colName, { name, format, options: field.options, translatable, visible: field.canFilter });
    });
  }

  private formatTooltip(criteria: RequestCriteria, deepLevel: number = 0): string {
    const formatter = criteria.fieldName ? this.tooltipFormatersMap.get(criteria.fieldName) : null;
    if ((formatter && !formatter.visible) ||
      (!criteria.criteria?.length && criteria.value == undefined && !criteria.start && !criteria.end)) return '';
    let name = formatter?.name ? formatter.name : (criteria.fieldName ? this.translate.instant(criteria.fieldName) : null);
    name = name ? name.toString().charAt(0).toUpperCase() + name.toString().slice(1) : name;
    const operator = this.translate.instant(criteria.operator);

    let value = formatter?.format && criteria.value != undefined ? formatter.format(criteria.value) : criteria.value;
    if (formatter?.options?.length) value = formatter.options.find(o => o.key === value?.toString())?.value ?? value;
    if (formatter?.translatable &&
      ((criteria.operator !== Operator.contains && criteria.operator !== Operator.iContains) ||
        formatter.options.some(o => o.key === criteria.value || o.value === criteria.value))) {
      value = value ? this.translate.instant(value) : value;
    }

    value = typeof value === 'string' ? this.stripHtml(value) : value;

    if (criteria.fieldName === 'area' && typeof value === 'string' && value.startsWith('POLYGON')) {
      const coordsMatch = /\(\((.*)\)\)/.exec(value);
      if (coordsMatch) {
        const points = coordsMatch[1].split(',');
        value = name + ':\n' + points
          .map(p => `   • ${p.trim().replace(' ', ', ')}`)
          .join('\n');
      }
    }

    const dateFormat = formatter?.format ?? ((d: string) => toVisibleDateTimeFormat(new Date(d)));
    const start = criteria.start ? dateFormat(criteria.start) : null;
    const end = criteria.end ? dateFormat(criteria.end) : null;
    if (criteria.criteria?.length) {
      const nextLevel = deepLevel + 1;
      const child = criteria.criteria.map(c => this.formatTooltip(c, nextLevel));
      const br = '      '.repeat(deepLevel);
      const op = `${br} - ${criteria.operator === Operator.or ? '||' : '&'} -`;
      const intended = child.map(c => br + c);
      return intended.join(`\n ${op} \n`);
    } else {
      return `${name ?? ''} | ${operator} | ${value ?? start}${end ? ' — ' + end : ''}`;
    }
  }

  private stripHtml(text: string): string {
    if (!text) return '';
    return text
      .replace(/<\/?[^>]+(>|$)/g, ' ')
      .replace(/\s\s+/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .trim();
  }
}

class TooltipFormatter {
  name: string;
  format: (value: string) => string;
  options: Option[];
  translatable: boolean;
  visible: boolean;
}
